import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";

import type { ApiErrorResponse } from "@/api/api.types";
import { PasswordRequirements } from "@/components/signUp";
import { useAuthentication } from "@/hooks";
import type { SignUpFormValuesType } from "@/types";
import { signUpFormSchema } from "@/types";
import { Button, FormField, Input, PasswordInput, Spinner } from "@/ui";
import { handleAxiosFieldErrors } from "@/utils";

export const SignUp = () => {
  const { invitationCode } = useParams();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm<SignUpFormValuesType>({
    resolver: zodResolver(signUpFormSchema),
  });
  const { useSignUp } = useAuthentication();
  const { mutate: signUp, isPending } = useSignUp();

  if (!invitationCode) return null;

  const onSubmit: SubmitHandler<SignUpFormValuesType> = ({
    fullName,
    password,
  }) => {
    signUp(
      { name: fullName, password, invitationCode },
      {
        onError: (error: unknown) => {
          const axiosError = error as AxiosError<ApiErrorResponse>;

          handleAxiosFieldErrors(axiosError, setError);
          if (!axiosError.response?.data?.error?.fields) {
            if (axiosError.response?.data?.error?.message) {
              return toast.error(axiosError.response?.data.error.message);
            }
            toast.error("An unexpected error occurred. Please try again.");
          }
        },
      },
    );
  };
  const password = watch("password") ?? "";

  return (
    <>
      <h1 className="text-center text-2xl font-bold text-primary-950">
        Complete your registration
      </h1>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-10">
        <FormField label="Full name" error={errors.fullName?.message}>
          <Input
            placeholder="Enter your name"
            {...register("fullName")}
            error={errors.fullName?.message}
          />
        </FormField>

        <FormField label="New password" error={errors.password?.message}>
          <PasswordInput
            placeholder="Enter your password"
            {...register("password")}
            error={errors.password?.message}
          />
        </FormField>

        <FormField
          label="Confirm new password"
          error={errors.passwordConfirmation?.message}
        >
          <PasswordInput
            placeholder="Enter your password"
            {...register("passwordConfirmation")}
            error={errors.passwordConfirmation?.message}
          />
        </FormField>
        <PasswordRequirements password={password} />
        <Button className="my-8 w-full font-semibold" type="submit">
          {isPending ? <Spinner /> : "Sign up"}
        </Button>
      </form>
    </>
  );
};
